import { mediaIdImageField } from '@/lib/mediaImageField';
import { AttractionTypeImageCell } from '@/components/attraction-types';
import type { CrudConfig } from '@startupquickstart/frontend';

// Define type based on our Prisma schema
export interface AttractionType {
  id: string;
  name: string;
  description?: string;
  featuredImageId?: string;
  createdAt: string;
  updatedAt: string;
  featuredImage?: {
    id: string;
    fileName: string;
    name: string;
    fileType: string;
    url?: string;
  };
  // Virtual field for large image display on detail pages
  featuredImageLarge?: {
    id: string;
    fileName: string;
    name: string;
    fileType: string;
    url?: string;
  };
}

/**
 * Attraction Type CRUD configuration
 */
export const attractionTypeCrudConfig: CrudConfig<AttractionType> = {
  entityName: 'Attraction Type',
  entityNamePlural: 'Attraction Types',

  endpoints: {
    list: '/attraction-types',
    single: (id: string) => `/attraction-types/${id}`,
    create: '/attraction-types',
    update: (id: string) => `/attraction-types/${id}`,
    delete: (id: string) => `/attraction-types/${id}`,
  },

  fields: [
    {
      key: 'name',
      label: 'Name',
      type: 'text',
      required: true,
      placeholder: 'Enter attraction type name',
      validation: {
        min: 2,
        max: 200,
      },
    },
    {
      key: 'description',
      label: 'Description',
      type: 'richtext',
      required: false,
      placeholder: 'Enter description',
    },
    {
      key: 'featuredImageId',
      label: 'Featured Image',
      type: 'image',
      ...mediaIdImageField,
      required: false,
      placeholder: 'Featured Image ID',
    },
    {
      key: 'featuredImage',
      label: 'Image',
      type: 'custom',
      required: false,
      format: (_value: AttractionType['featuredImage'], fullRecord?: AttractionType) => {
        return <AttractionTypeImageCell attractionType={fullRecord} />;
      },
    },
    {
      key: 'featuredImageLarge',
      label: 'Image',
      type: 'custom',
      required: false,
      format: (_value: AttractionType['featuredImage'], fullRecord?: AttractionType) => {
        return <AttractionTypeImageCell attractionType={fullRecord} size="lg" />;
      },
    },
    {
      key: 'createdAt',
      label: 'Created At',
      type: 'datetime',
      readonly: true,
    },
    {
      key: 'updatedAt',
      label: 'Updated At',
      type: 'datetime',
      readonly: true,
    },
  ],

  listView: {
    fields: ['featuredImage', 'name', 'createdAt', 'updatedAt'],
    searchableFields: ['name'],
    sortableFields: ['name', 'createdAt', 'updatedAt'],
    defaultSort: {
      field: 'name',
      direction: 'asc',
    },
    itemsPerPage: 10,
    actions: {
      create: {
        enabled: true,
      },
      view: {
        enabled: true,
      },
      edit: {
        enabled: true,
      },
      delete: {
        enabled: true,
      },
    },
  },

  detailView: {
    sections: [
      {
        title: 'Basic Information',
        description: 'Core attraction type details',
        fields: ['featuredImageLarge', 'name', 'description'],
        defaultExpanded: true,
      },
      {
        title: 'System Information',
        description: 'Timestamps and system data',
        fields: ['createdAt', 'updatedAt'],
        defaultExpanded: true,
      },
    ],
  },

  form: {
    sections: [
      {
        title: 'General',
        description: 'General Information about the Attraction Type',
        fields: ['featuredImageId', 'name', 'description'],
        columns: 1,
      },
    ],
    validation: {
      name: {
        required: true,
        min: 2,
        max: 200,
      },
    },
  },
};
